import { useState, useEffect } from 'react'
import { IoMdWarning } from 'react-icons/io'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/react'
import locationsData from '../../../../src/data/locations.json'
import MyButton from '../../../components/buttons'
import { FaTrash } from 'react-icons/fa'
import { Checkbox, FormControlLabel, FormGroup } from '@mui/material'
import Map from '../../../../src/components/Map'

interface UserLocationFormProps {
  formData: any
  handleFieldChange: (key: string, value: any) => void
}

interface FormData {
  move: boolean
  location: string[]
  escort?: boolean
  [key: string]: any
}

const UserLocationForm = ({
  formData: initialFormData,
  handleFieldChange,
}: UserLocationFormProps) => {
  const { t } = useTranslation()
  const [updateComponent, setUpdateComponent] = useState(false)

  const getStorageKey = () => {
    const currentPath = window.location.pathname
    const basePath = currentPath.split('?')[0].split('/').filter(Boolean)[1]

    const keyMap: { [key: string]: string } = {
      'announce-work': 'myJobOffer',
      'announce-event': 'myEvent',
      'announce-establishment': 'myEstablishment',
    }

    return keyMap[basePath as keyof typeof keyMap] || 'myAnnounce'
  }

  const storageKey = getStorageKey()

  const [formData, setFormData] = useState<FormData>(() => {
    const savedFormData = JSON.parse(localStorage.getItem(storageKey) || '{}')
    if (savedFormData.escort === undefined) {
      savedFormData.escort = false
      localStorage.setItem(storageKey, JSON.stringify(savedFormData))
    }
    const move =
      savedFormData.escort !== undefined ? savedFormData.escort : true
    const location = savedFormData.location || (move ? [] : ['noEscort'])

    return {
      ...initialFormData,
      ...savedFormData,
      move,
      location,
    }
  })

  const renderLocations = () => {
    return (
      <div className='flex flex-col sm:flex-wrap sm:flex-row'>
        {formData.location
          .filter(locationKey => locationKey !== 'noEscort')
          .map((locationKey, index) => (
            <div
              onClick={() => handleRemoveProvince(locationKey)}
              className='rounded-lg cursor-pointer relative group 
                  hover:bg-redpink hover:text-white mb-2 sm:mb-0 
                  px-4 py-2 text-center w-full sm:w-1/2'
              key={`${locationKey}-${index}`}
            >
              {t(`provinces.${locationKey}`)}
              <FaTrash
                className='absolute top-1/2 right-2 transform -translate-y-1/2 
                     opacity-0 group-hover:opacity-100 text-white'
              />
            </div>
          ))}
      </div>
    )
  }

  const updateLocalStorage = (updates: Partial<FormData>) => {
    try {
      const currentData = JSON.parse(localStorage.getItem(storageKey) || '{}')
      const newData = { ...currentData, ...updates }
      localStorage.setItem(storageKey, JSON.stringify(newData))
      handleFieldChange('location', updates.location)
      if (updates.escort !== undefined) {
        handleFieldChange('escort', updates.escort)
      }
    } catch (e) {
      Sentry.captureException(e, {
        extra: {
          action: 'updateLocalStorage',
          updates,
          timestamp: new Date().toISOString(),
        },
        tags: {
          type: 'local_storage_error',
          method: 'updateLocalStorage',
        },
      })
    }
  }
  useEffect(() => {
    const savedFormData = JSON.parse(localStorage.getItem(storageKey) || '{}')

    if (savedFormData.escort === undefined) {
      savedFormData.escort = false
      localStorage.setItem(storageKey, JSON.stringify(savedFormData))
    }

    const move = savedFormData.location && savedFormData.location.length > 0

    setFormData(prev => ({
      ...prev,
      ...savedFormData,
      move,
      escort: move,
    }))
  }, [storageKey])

  useEffect(() => {
    const handleStorageChange = () => {
      const savedFormData = JSON.parse(localStorage.getItem(storageKey) || '{}')
      setFormData(prev => ({
        ...prev,
        ...savedFormData,
        move: savedFormData.escort !== undefined ? savedFormData.escort : true,
        location: savedFormData.location || [],
      }))
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [storageKey])

  const handleMoveChange = (move: boolean) => {
    setFormData(prevFormData => {
      if (prevFormData.move === move) return prevFormData

      const updatedLocations = move ? [] : ['noEscort']
      const updatedFormData = {
        ...prevFormData,
        move,
        location: updatedLocations,
        escort: move,
      }

      updateLocalStorage({
        escort: move,
        location: updatedLocations,
      })

      return updatedFormData
    })
  }

  const handleProvinceClick = (id: string) => {
    setFormData(prevFormData => {
      const updatedLocations = prevFormData.location.includes(id)
        ? prevFormData.location.filter(loc => loc !== id)
        : [...prevFormData.location, id]

      updateLocalStorage({
        location: updatedLocations,
        escort: prevFormData.move,
      })

      return {
        ...prevFormData,
        location: updatedLocations,
      }
    })
  }

  const handleRemoveField = (event: React.MouseEvent) => {
    event.preventDefault()
    const updatedLocations = formData.move ? [] : ['noEscort']
    setFormData(prev => ({ ...prev, location: updatedLocations }))
    updateLocalStorage({
      location: updatedLocations,
      escort: formData.move,
    })
    setUpdateComponent(prev => !prev)
  }

  const handleSelectAllClick = () => {
    const allProvinces = Object.keys(locationsData.location)
    const newLocations =
      formData.location.length === allProvinces.length ? [] : allProvinces

    setFormData(prev => ({ ...prev, location: newLocations }))
    updateLocalStorage({
      location: newLocations,
      escort: formData.move,
    })
  }

  const handleRemoveProvince = (locationKey: string) => {
    const updatedLocations = formData.location.filter(
      loc => loc !== locationKey,
    )
    setFormData(prev => ({ ...prev, location: updatedLocations }))
    updateLocalStorage({
      location: updatedLocations,
      escort: formData.move,
    })
  }

  return (
    <>
      <FormGroup className='justify-center'>
        <div className='gap-2 w-full flex mb-2 flex-col sm:flex-row'>
          <div className='justify-center text-anthracite bg-grisclair flex w-full rounded-lg sm:mb-0'>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.move}
                  onChange={() => handleMoveChange(true)}
                />
              }
              label={t('create-ad.escort-true')}
            />
          </div>
          <div className='justify-center text-anthracite bg-grisclair flex w-full rounded-lg'>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!formData.move}
                  onChange={() => handleMoveChange(false)}
                />
              }
              label={t('create-ad.escort-false')}
            />
          </div>
        </div>
      </FormGroup>

      {!formData.move && (
        <div className='flex flex-col sm:flex-row items-center justify-center bg-grisclair text-anthracite px-4 py-2 rounded-lg'>
          <IoMdWarning className='text-2xl sm:text-3xl mr-2 sm:mr-4 text-redpink' />
          <span className='text-center sm:text-left text-sm lg:text-base'>
            {t('create-ad.advertiser-no-escort')}
          </span>
        </div>
      )}

      {formData.move && (
        <div className='flex flex-col lg:flex-row'>
          <div className='w-full mb-2  sm:mr-4'>
            <Map
              selectedProvinces={formData.location}
              setSelectedProvinces={renderLocations}
              onProvinceClick={handleProvinceClick}
            />
          </div>
          <div className='field-container bg-grisclair p-2 rounded-lg w-full text-center mb-2'>
            <div className='mb-0 p-2 bg-grisclair2 rounded-lg'>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      formData.location.length ===
                      Object.keys(locationsData.location).length
                    }
                    onChange={handleSelectAllClick}
                    className='form-checkbox h-5 w-5 text-redpink'
                  />
                }
                label={
                  <span className='cursor-pointer text-anthracite '>
                    {t('create-ad.select-all')}
                  </span>
                }
                className='flex items-center space-x-2'
              />
            </div>
            {renderLocations()}
            {formData.location.length > 0 && (
              <MyButton
                variant='redpink-anthracite'
                className='mt-2'
                onClick={handleRemoveField}
              >
                {t('create-ad.delete-all')}
              </MyButton>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export default UserLocationForm
